import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Plus, Minus, ShoppingCart, MapPin, Clock } from 'lucide-react';
import { PinPad, SuccessScreen, SectionCard } from './GDLComponents';
import { useGDLData } from './useGDLData';
import APIPartnerStrip from './APIPartnerStrip';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VENDOR_DATA = {
  Feeding: {
    supermarkets: [
      { name: 'Boku', tag: 'Premium', gradient: 'linear-gradient(135deg, #1a472a, #2d6a4f)' },
      { name: 'Spar', tag: 'Nationwide', gradient: 'linear-gradient(135deg, #1d3557, #457b9d)' },
      { name: 'Ebeano', tag: 'Local Fave', gradient: 'linear-gradient(135deg, #3d2b1f, #6f4e37)' },
    ],
    markets: [
      { name: 'Mile 12 Market', location: 'Ketu, Lagos', bestFor: 'Bulk foodstuffs', bestTime: '4AM\u20137AM' },
      { name: 'Oyingbo Market', location: 'Ebute Metta', bestFor: 'Fresh fish & meat', bestTime: '5AM\u20138AM' },
      { name: 'Mushin Market', location: 'Mushin, Lagos', bestFor: 'Spices & peppers', bestTime: '6AM\u20139AM' },
      { name: 'Ikotun Market', location: 'Ikotun, Lagos', bestFor: 'Fresh produce', bestTime: '5AM\u20138AM' },
    ],
  },
  Transport: {
    vendors: [
      { name: 'Bolt', tag: 'Rides', gradient: 'linear-gradient(135deg, #3d2f00, #7d5f00)' },
      { name: 'Shuttlers', tag: 'Commute', gradient: 'linear-gradient(135deg, #0d3348, #1a6b8a)' },
      { name: 'Moove', tag: 'Rentals', gradient: 'linear-gradient(135deg, #1a1a2e, #16213e)' },
      { name: 'GoMyWay', tag: 'Carpooling', gradient: 'linear-gradient(135deg, #1a3a1a, #2d6b2d)' },
    ],
  },
  Housing: {
    vendors: [
      { name: 'Mixta', tag: 'Development', gradient: 'linear-gradient(135deg, #1e2d40, #2e4a6b)' },
      { name: 'RevvUp', tag: 'Furniture', gradient: 'linear-gradient(135deg, #3d2000, #7a4000)' },
      { name: 'Handy', tag: 'Services', gradient: 'linear-gradient(135deg, #1a1a1a, #404040)' },
    ],
  },
};

const PRODUCTS = {
  Boku: [
    { id: 'b1', name: 'Premium Rice (5kg)', price: 4500 },
    { id: 'b2', name: 'Whole Chicken', price: 3800 },
    { id: 'b3', name: 'Vegetable Oil (3L)', price: 5200 },
    { id: 'b4', name: 'Fresh Tomatoes Box', price: 2500 },
  ],
  Spar: [
    { id: 's1', name: 'Pasta Pack (3x)', price: 2800 },
    { id: 's2', name: 'Milk & Cereal Bundle', price: 4200 },
    { id: 's3', name: 'Fruit Basket', price: 3500 },
    { id: 's4', name: 'Household Pack', price: 6800 },
  ],
  Ebeano: [
    { id: 'e1', name: 'Yam Tubers (3x)', price: 3000 },
    { id: 'e2', name: 'Fresh Fish Set', price: 4500 },
    { id: 'e3', name: 'Plantain Bunch', price: 1200 },
    { id: 'e4', name: 'Garri (10kg)', price: 3500 },
  ],
  Bolt: [
    { id: 'bt1', name: 'Weekly Pass (5 rides)', price: 7500 },
    { id: 'bt2', name: 'Monthly Pass (20 rides)', price: 25000 },
  ],
  Shuttlers: [
    { id: 'sh1', name: 'Monthly Route Pass', price: 18000 },
    { id: 'sh2', name: 'Weekly Route Pass', price: 5500 },
  ],
  Moove: [
    { id: 'mv1', name: 'Weekend Rental (2 days)', price: 15000 },
    { id: 'mv2', name: 'Weekly Rental', price: 35000 },
  ],
  GoMyWay: [
    { id: 'gm1', name: '10-ride Bundle', price: 8000 },
    { id: 'gm2', name: '20-ride Bundle', price: 14000 },
  ],
  Mixta: [
    { id: 'mx1', name: 'Monthly Savings Plan', price: 30000 },
    { id: 'mx2', name: 'Quarterly Payment', price: 75000 },
  ],
  RevvUp: [
    { id: 'rv1', name: 'Office Chair', price: 18000 },
    { id: 'rv2', name: 'Desk Setup', price: 35000 },
  ],
  Handy: [
    { id: 'hd1', name: 'Plumbing Service', price: 8000 },
    { id: 'hd2', name: 'Electrical Fix', price: 6000 },
  ],
};

function VendorCard({ vendor, onClick }) {
  return (
    <button onClick={onClick} data-testid={`vendor-${vendor.name}`} style={{
      width: '100%', height: 180, borderRadius: 18, overflow: 'hidden', border: 'none',
      background: vendor.gradient, position: 'relative', cursor: 'pointer', flexShrink: 0,
      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: 18,
      transition: 'transform 0.15s ease',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.5) 100%)' }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', borderRadius: 6, padding: '3px 10px', fontSize: 10, color: '#fff', fontWeight: 600, marginBottom: 6 }}>
          {vendor.tag}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{vendor.name}</div>
        <div style={{
          display: 'inline-block', marginTop: 8, padding: '6px 14px', border: '1.5px solid rgba(255,255,255,0.5)',
          borderRadius: 8, fontSize: 12, color: '#fff', fontWeight: 600,
        }}>
          Shop Now
        </div>
      </div>
    </button>
  );
}

function MarketCard({ market, onOrder }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: 16,
      boxShadow: '0 2px 10px rgba(176,49,64,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>{'\uD83C\uDFEA'}</span>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#2D0A10' }}>{market.name}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#A07078', marginBottom: 4 }}>
        <MapPin size={12} /> {market.location}
      </div>
      <div style={{ fontSize: 12, color: '#2D0A10', marginBottom: 4 }}>Best for: {market.bestFor}</div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(176,49,64,0.06)', borderRadius: 6, padding: '3px 8px', fontSize: 11, color: '#B03140', fontWeight: 600, marginBottom: 10 }}>
        <Clock size={10} /> {market.bestTime}
      </div>
      <button onClick={onOrder} data-testid={`market-${market.name}`} style={{
        display: 'block', width: '100%', padding: '10px', background: '#B03140', color: '#fff',
        border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(176,49,64,0.2)',
      }}>
        Order Now
      </button>
    </div>
  );
}

export default function GDLVendors() {
  const nav = useNavigate();
  const [category, setCategory] = useState('Feeding');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [cart, setCart] = useState({});
  const [step, setStep] = useState('browse');
  const [paySource, setPaySource] = useState('wallet');
  const [result, setResult] = useState(null);
  const { csId, refresh } = useGDLData();

  const categories = [
    { key: 'Feeding', label: '\uD83C\uDF7D\uFE0F Feeding' },
    { key: 'Transport', label: '\uD83D\uDE97 Transport' },
    { key: 'Housing', label: '\uD83C\uDFE0 Housing' },
  ];

  const products = selectedVendor ? (PRODUCTS[selectedVendor.name] || []) : [];
  const cartItems = Object.entries(cart).filter(([_, qty]) => qty > 0);
  const subtotal = cartItems.reduce((s, [id, qty]) => {
    const p = products.find(p => p.id === id);
    return s + (p ? p.price * qty : 0);
  }, 0);
  const delivery = subtotal > 10000 ? 0 : 1500;
  const total = subtotal + delivery;

  const updateCart = (id, delta) => {
    setCart(c => ({ ...c, [id]: Math.max(0, (c[id] || 0) + delta) }));
  };

  const handlePay = async (pin) => {
    if (pin !== '123456') { setStep('browse'); return; }
    try {
      const catMap = { Feeding: 'Feeding', Transport: 'Transport', Housing: 'Housing' };
      const { data } = await axios.post(`${API}/api/demo/gdl/bills/${csId}`, {
        category: catMap[category] || 'Feeding',
        provider: selectedVendor?.name || 'Vendor',
        identifier: `order-${Date.now()}`,
        amount: total,
      }, { withCredentials: true });
      setResult(data);
      setStep('success');
      refresh();
    } catch (e) {
      setStep('browse');
    }
  };

  const resetFlow = () => {
    setSelectedVendor(null);
    setCart({});
    setStep('browse');
    setResult(null);
  };

  if (!csId) { nav('/demo/gdl/login'); return null; }

  // Product list view
  if (selectedVendor && step === 'products') {
    return (
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <button onClick={() => { setSelectedVendor(null); setCart({}); setStep('browse'); }} style={{
          display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
          color: '#A07078', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0,
        }}>
          <ChevronLeft size={18} /> Back to Vendors
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#2D0A10' }}>{selectedVendor.name}</div>
          <span style={{ background: 'rgba(176,49,64,0.08)', color: '#B03140', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{selectedVendor.tag}</span>
        </div>
        <SectionCard>
          {products.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(176,49,64,0.05)' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#2D0A10' }}>{p.name}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#B03140' }}>{'\u20A6'}{p.price.toLocaleString()}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => updateCart(p.id, -1)} disabled={!cart[p.id]} style={qtyBtn}><Minus size={14} /></button>
                <span style={{ fontSize: 14, fontWeight: 700, minWidth: 20, textAlign: 'center', color: '#2D0A10' }}>{cart[p.id] || 0}</span>
                <button onClick={() => updateCart(p.id, 1)} style={qtyBtn}><Plus size={14} /></button>
              </div>
            </div>
          ))}
        </SectionCard>
        {cartItems.length > 0 && (
          <button onClick={() => setStep('cart')} data-testid="view-cart-btn" style={{
            position: 'sticky', bottom: 16, width: '100%', padding: '14px',
            background: '#B03140', color: '#fff', border: 'none', borderRadius: 14,
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: '0 4px 16px rgba(176,49,64,0.3)',
          }}>
            <ShoppingCart size={18} />
            {cartItems.reduce((s, [_, q]) => s + q, 0)} items {'\u00B7'} {'\u20A6'}{subtotal.toLocaleString()} {'\u2014'} View Cart
          </button>
        )}
      </div>
    );
  }

  // Cart view
  if (step === 'cart') {
    return (
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <button onClick={() => setStep('products')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#A07078', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
          <ChevronLeft size={18} /> Back to Products
        </button>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#2D0A10' }}>Your Cart</div>
        <SectionCard>
          {cartItems.map(([id, qty]) => {
            const p = products.find(p => p.id === id);
            if (!p) return null;
            return (
              <div key={id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(176,49,64,0.05)' }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#2D0A10' }}>{p.name} x{qty}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2D0A10' }}>{'\u20A6'}{(p.price * qty).toLocaleString()}</div>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(176,49,64,0.06)' }}>
            <span style={{ fontSize: 14, color: '#A07078' }}>Subtotal</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#2D0A10' }}>{'\u20A6'}{subtotal.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(176,49,64,0.06)' }}>
            <span style={{ fontSize: 14, color: '#A07078' }}>Delivery</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#2D0A10' }}>{delivery === 0 ? 'Free \u2014 Walk-in' : `\u20A6${delivery.toLocaleString()}`}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#2D0A10' }}>Total</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#B03140' }}>{'\u20A6'}{total.toLocaleString()}</span>
          </div>
        </SectionCard>

        {/* Payment Source */}
        <SectionCard title="Payment Source">
          {[
            { key: 'benefit', label: `${category === 'Feeding' ? '\uD83C\uDF7D\uFE0F Food' : category === 'Transport' ? '\uD83D\uDE97 Transport' : '\uD83C\uDFE0 Housing'} Allowance`, sub: 'Available' },
            { key: 'wallet', label: '\uD83D\uDCB3 Wallet', sub: 'Available' },
          ].map(s => (
            <button key={s.key} onClick={() => setPaySource(s.key)} style={{
              display: 'flex', alignItems: 'center', width: '100%', padding: '12px 14px',
              borderRadius: 12, border: paySource === s.key ? '2px solid #B03140' : '1.5px solid rgba(176,49,64,0.1)',
              background: paySource === s.key ? 'rgba(176,49,64,0.04)' : '#fff', marginBottom: 8, cursor: 'pointer',
            }}>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#2D0A10' }}>{s.label}</div>
                <div style={{ fontSize: 12, color: '#A07078' }}>{s.sub}</div>
              </div>
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                border: paySource === s.key ? '6px solid #B03140' : '2px solid rgba(176,49,64,0.15)',
              }} />
            </button>
          ))}
        </SectionCard>

        <button onClick={() => setStep('pin')} data-testid="pay-now-btn" style={{
          width: '100%', padding: '14px', background: '#B03140', color: '#fff',
          border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(176,49,64,0.25)',
        }}>
          Pay Now
        </button>
      </div>
    );
  }

  // PIN
  if (step === 'pin') {
    return (
      <div style={{ padding: 16 }}>
        <SectionCard>
          <PinPad onComplete={handlePay} title="Enter PIN to confirm" />
        </SectionCard>
      </div>
    );
  }

  // Success
  if (step === 'success') {
    return (
      <div style={{ padding: 16 }}>
        <SectionCard>
          <SuccessScreen
            title="Payment Successful"
            subtitle={`\u20A6${total.toLocaleString()} paid to ${selectedVendor?.name}`}
            reference={result?.reference || 'VND-' + Date.now()}
            buttonLabel="Continue Shopping"
            onDone={resetFlow}
          />
        </SectionCard>
      </div>
    );
  }

  // Browse vendors
  const catData = VENDOR_DATA[category];
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ animation: 'gdlFadeIn 0.3s ease' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#2D0A10', letterSpacing: '-0.02em' }}>Vendors</div>
        <div style={{ fontSize: 14, color: '#A07078', marginTop: 4 }}>Use your benefits at partner stores</div>
      </div>

      {/* ── Session 2: API partners + self-onboarded merchants ── */}
      <APIPartnerStrip csId={csId} onPurchased={refresh} />

      {/* Category Pills */}
      <div style={{ display: 'flex', gap: 8 }}>
        {categories.map(c => (
          <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`vendor-cat-${c.key}`}
            style={{
              padding: '9px 18px', borderRadius: 24, fontSize: 13, fontWeight: 600,
              border: 'none',
              background: category === c.key ? '#B03140' : '#fff',
              color: category === c.key ? '#fff' : '#8E2233', cursor: 'pointer',
              boxShadow: category === c.key ? '0 4px 12px rgba(176,49,64,0.25)' : '0 1px 4px rgba(176,49,64,0.06)',
              transition: 'all 0.15s ease',
            }}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Supermarket vendor cards (horizontal scroll) */}
      {category === 'Feeding' && catData.supermarkets && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#A07078' }}>Supermarkets</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingBottom: 8 }}>
            {catData.supermarkets.map(v => (
              <VendorCard key={v.name} vendor={v} onClick={() => { setSelectedVendor(v); setCart({}); setStep('products'); }} />
            ))}
          </div>
        </>
      )}

      {/* Markets (Feeding only) */}
      {category === 'Feeding' && catData.markets && (
        <>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#A07078', marginBottom: 4 }}>Lagos Fresh Markets</div>
            <div style={{ fontSize: 13, color: '#A07078', marginBottom: 12 }}>Order from Lagos{'\u2019'}s biggest food markets {'\u2014'} same-day delivery</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {catData.markets.map(m => (
              <MarketCard key={m.name} market={m} onOrder={() => { setSelectedVendor({ name: 'Boku', tag: 'Market' }); setCart({}); setStep('products'); }} />
            ))}
          </div>
        </>
      )}

      {/* Transport / Housing vendor cards */}
      {(category === 'Transport' || category === 'Housing') && catData.vendors && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {catData.vendors.map(v => (
            <VendorCard key={v.name} vendor={v} onClick={() => { setSelectedVendor(v); setCart({}); setStep('products'); }} />
          ))}
        </div>
      )}
    </div>
  );
}

const qtyBtn = {
  width: 34, height: 34, borderRadius: '50%', border: 'none',
  background: 'rgba(176,49,64,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#B03140',
};
